'use client';

import { TChipSize, TChipTone } from '@umodeapporg/ui/components/UI/Chips/chipTones';
import OutlinedChip from '@umodeapporg/ui/components/UI/Chips/OutlinedChip';
import type { SymbolCodepoints } from '@umodeapporg/ui/components/UI/MaterialSymbol';
import { useTranslation } from 'react-i18next';

import { FonteTempo } from '@/domain/ops/interfaces/ops';

/** How one time source is painted wherever a source is named. */
interface IFonteVisual {
  /** The library tone, where one of them already means what the source means. */
  tone?: TChipTone;
  /** Chip and dot classes, for the source no library tone fits. */
  className?: string;
  dotClassName?: string;
  /** Replaces the dot where the source is not a reading of the floor. */
  icon?: SymbolCodepoints;
}

/**
 * ONE colour per source: two sources painted alike read as the same fact, which is what
 * used to happen here (the OP override shared VESTO's blue, and the measured sequence
 * shared the product VESTO's green). Blue stays the OP's own VESTO minute and green the
 * product's; an estimated sequence is amber, because the date exists but is not
 * chronometered yet, and no time at all is red.
 *
 * Two of them are deliberately not a plain tone:
 *
 * - The OP override is graphite plus a padlock. It says "a person pinned this number",
 *   a manual exception, and never a reading like the other five.
 * - The measured sequence owns the theme's indigo. The library's `indigo` chip tone
 *   paints the brand purple, the colour of every title on these screens, so the chip
 *   names the indigo of the categorical scale: the one blue-violet in the theme that no
 *   other source and no heading uses, and that follows light and dark on its own.
 * - The informed sequence takes the teal of the same categorical scale, for the same
 *   reason: the number a person in the factory launched is neither the measurement
 *   (indigo) nor the catalogue guess (amber), and painting it like either of them would
 *   say it is one of them. Teal and not the scale's magenta because magenta sits next to
 *   the red of "no time at all", and a launched number is not an alarm. Its wording
 *   spells out that nobody timed it. The product's informed total shares that teal and
 *   carries a pencil: it is the SAME fact (a number somebody launched), and the icon is
 *   what says it came from the product instead of from a route.
 *
 * This map is the single source of the colour: the Carteira table and its source card,
 * the OP panel, the home card and the reliability report all read a source from here.
 */
const FONTE_VISUAL: Record<FonteTempo, IFonteVisual> = {
  CURADO_OP: { tone: 'neutral', icon: 'lock' },
  SEQUENCIA_MEDIDA: { className: 'bg-chart-8/10 text-chart-8', dotClassName: 'bg-chart-8' },
  SEQUENCIA_INFORMADA: { className: 'bg-chart-2/10 text-chart-2', dotClassName: 'bg-chart-2' },
  TEMPO_INFORMADO_PRODUTO: {
    className: 'bg-chart-2/10 text-chart-2',
    dotClassName: 'bg-chart-2',
    icon: 'edit_note',
  },
  // O ritmo travado da linha é o MESMO degrau do informado, por outro caminho: divide o
  // teal, e o cronômetro de velocidade é o que diz que o número veio de peças por hora e
  // não de um mm:ss digitado.
  RITMO_PRODUTO: {
    className: 'bg-chart-2/10 text-chart-2',
    dotClassName: 'bg-chart-2',
    icon: 'speed',
  },
  VESTO: { tone: 'info' },
  VESTO_PRODUTO: { tone: 'success' },
  // O emprestimo de OUTRO produto: roxo proprio mais a seta que diz "veio de la". Nao
  // divide o teal do informado porque nao e um numero deste produto, e nao divide o
  // ambar do estimado porque nao e chute: e o tempo real de um parecido.
  PRODUTO_SIMILAR: { tone: 'purple', icon: 'swap_horiz' },
  SEQUENCIA_ESTIMADA: { tone: 'warning' },
  NENHUMA: { tone: 'danger' },
};

/** The padlock follows the label size: the library's 16px default swamps an xs chip. */
const ICON_SIZE: Record<TChipSize, number> = { xs: 14, sm: 16, md: 18 };

interface FonteTempoChipProps {
  fonte: FonteTempo;
  size?: TChipSize;
}

/**
 * Where a minute per piece came from: one OP's time, or a whole slice of the portfolio.
 * The chip is a short label, so it reads `Seq. (estimada)`; the long wording stays on
 * the screens that spell the source out in a sentence.
 */
export function FonteTempoChip({ fonte, size = 'xs' }: FonteTempoChipProps) {
  const { t } = useTranslation('common');
  const { tone, className, dotClassName, icon } = FONTE_VISUAL[fonte];

  return (
    <OutlinedChip
      label={t(`ops.fonteTempoCurto.${fonte}`)}
      tone={tone}
      className={className}
      dotClassName={dotClassName}
      variant="soft"
      size={size}
      dot={icon === undefined}
      chipIcon={icon}
      chipIconSize={ICON_SIZE[size]}
      preserveLabelCase
    />
  );
}
